import React, {useCallback, useEffect, useRef} from 'react';
import {Vector3} from './types.ts';
import {
    GizmoMode,
    SceneCameraState,
    parseSceneMessage,
    postToScene
} from './sceneMessages.ts';
import {SceneFrameHandle} from './frame/SceneFrame.tsx';

// How long getCamera waits for the scene to answer. Scripts older than
// runtime 3 never do.
const CAMERA_REPLY_TIMEOUT_MS = 2000;

const isVector3 = (value: unknown): value is Vector3 =>
    !!value &&
    typeof value === 'object' &&
    ['x', 'y', 'z'].every(axis =>
        Number.isFinite((value as Record<string, unknown>)[axis])
    );

interface SceneTransform {
    position?: Vector3;
    rotation?: Vector3;
    scaling?: Vector3;
}

interface UseSceneMessagingOptions {
    frameRef: React.RefObject<SceneFrameHandle>;
    enabled: boolean;
    selectedElementId: string | null;
    gizmoMode: GizmoMode;
    onSelected: (elementId: string) => void;
    onTransform: (elementId: string, transform: SceneTransform) => void;
}

// Wires the editor panel to the scene iframe over postMessage:
//  - listens for scene -> editor messages (ready / selected / transform /
//    cameraState)
//  - on every "ready" it re-sends enableEditing + current selection + mode so a
//    reloaded or late-loading iframe re-attaches the gizmo correctly
//  - returns helpers the panel calls in response to user actions
export default function useSceneMessaging({
    frameRef,
    enabled,
    selectedElementId,
    gizmoMode,
    onSelected,
    onTransform
}: UseSceneMessagingOptions) {
    // Refs keep the message listener and the "ready" re-sync free of stale values.
    const selectedRef = useRef(selectedElementId);
    const gizmoModeRef = useRef(gizmoMode);
    const onSelectedRef = useRef(onSelected);
    const onTransformRef = useRef(onTransform);
    // getCamera calls waiting for the scene's cameraState reply.
    const cameraRequestsRef = useRef<
        ((state: SceneCameraState | null) => void)[]
    >([]);

    selectedRef.current = selectedElementId;
    gizmoModeRef.current = gizmoMode;
    onSelectedRef.current = onSelected;
    onTransformRef.current = onTransform;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const syncScene = () => {
            postToScene(frameRef.current, {action: 'enableEditing'});
            postToScene(frameRef.current, {
                action: 'setGizmoMode',
                mode: gizmoModeRef.current
            });
            postToScene(frameRef.current, {
                action: 'select',
                elementId: selectedRef.current
            });
        };

        const handleMessage = (event: MessageEvent) => {
            const message = parseSceneMessage(event, frameRef.current);
            if (!message) {
                return;
            }

            switch (message.action) {
                case 'ready':
                    syncScene();
                    break;
                case 'selected':
                    onSelectedRef.current(message.elementId);
                    break;
                case 'transform':
                    onTransformRef.current(message.elementId, {
                        position: message.position,
                        rotation: message.rotation,
                        scaling: message.scaling
                    });
                    break;
                case 'cameraState':
                    if (
                        isVector3(message.position) &&
                        isVector3(message.target) &&
                        Number.isFinite(message.fov)
                    ) {
                        const requests = cameraRequestsRef.current;
                        cameraRequestsRef.current = [];
                        requests.forEach(answer =>
                            answer({
                                position: message.position,
                                target: message.target,
                                fov: message.fov
                            })
                        );
                    }
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        // Cover the case where the scene loaded before this listener mounted:
        // ping it and it replies "ready", which triggers syncScene above.
        postToScene(frameRef.current, {action: 'ping'});

        return () => window.removeEventListener('message', handleMessage);
    }, [enabled, frameRef]);

    const highlight = useCallback(
        (elementId: string | null) =>
            postToScene(frameRef.current, {action: 'highlight', elementId}),
        [frameRef]
    );

    const select = useCallback(
        (elementId: string | null) =>
            postToScene(frameRef.current, {action: 'select', elementId}),
        [frameRef]
    );

    const setGizmoMode = useCallback(
        (mode: GizmoMode) =>
            postToScene(frameRef.current, {action: 'setGizmoMode', mode}),
        [frameRef]
    );

    // Asks the scene where its camera is. Resolves null if it doesn't answer.
    const getCamera = useCallback(
        () =>
            new Promise<SceneCameraState | null>(resolve => {
                let timeout = 0;
                const answer = (state: SceneCameraState | null) => {
                    window.clearTimeout(timeout);
                    resolve(state);
                };
                timeout = window.setTimeout(() => {
                    cameraRequestsRef.current =
                        cameraRequestsRef.current.filter(a => a !== answer);
                    answer(null);
                }, CAMERA_REPLY_TIMEOUT_MS);
                cameraRequestsRef.current.push(answer);
                postToScene(frameRef.current, {action: 'getCamera'});
            }),
        [frameRef]
    );

    return {highlight, select, setGizmoMode, getCamera};
}
